import React from 'react';

import type { ColumnDef } from '@tanstack/react-table';

import Checkbox from 'components/partials/checkbox/checkbox';
import IconButton from 'components/partials/button/icon-button';
import Tooltip from 'components/partials/tooltip/tooltip';

import Icons from 'assets/icons';

import { buildPreviousAwardsReasonsText } from '../helpers/results-format';
import type { DisplayNonModeledRow } from '../hooks/use-non-modeled-adjustments';
import type { NonModeledCells } from '../results-step.types';

import EditableEnrollmentCell from './non-modeled/editable-enrollment-cell';
import EditableDiscountRateCell from './non-modeled/editable-discount-rate-cell';

type ValueField = keyof Omit<
  NonModeledCells,
  'enrollment' | 'tuitionDiscountRate'
>;

interface GetNonModeledTableSettingsParams {
  onEnrollmentChange: (row: DisplayNonModeledRow, value: number) => void;
  onDiscountRateChange: (row: DisplayNonModeledRow, value: number) => void;
  onRepackageToggle: (row: DisplayNonModeledRow, value: boolean) => void;
  onRepackageToggleAll: (value: boolean) => void;
  onRowUndo: (row: DisplayNonModeledRow) => void;
  allRepackaged: boolean;
  someRepackaged: boolean;
  readOnly?: boolean;
}

const commonHeaderStyles =
  'font-semibold cursor-default text-xs py-3 uppercase text-left';

const renderValueCell = (row: DisplayNonModeledRow, field: ValueField) => (
  <div className="flex flex-col items-start justify-center">
    {row.isPreviewing ? (
      <Icons.Spinner className="size-6 animate-spin" aria-hidden="true" />
    ) : (
      <span className="text-base text-gray-700 leading-6">{row[field]}</span>
    )}
    {row.lastYear && (
      <span className="text-sm text-gray-500 leading-5">
        {row.lastYear[field]}
      </span>
    )}
  </div>
);

const renderUsingPreviousAwards = (row: DisplayNonModeledRow) => {
  if (row.isPreviewing) {
    return (
      <Icons.Spinner
        className="size-4 shrink-0 animate-spin"
        aria-hidden="true"
      />
    );
  }

  const notIncludedStudents = row.repackageStatus?.notIncludedStudents ?? 0;

  if (!row.repackageWithNewRegimes || !notIncludedStudents) {
    return null;
  }

  const label = `Using previous awards: ${notIncludedStudents}`;
  const reasonsText = buildPreviousAwardsReasonsText(row.repackageStatus);

  return (
    <Tooltip content={reasonsText} enabled={!!reasonsText} position="bottom">
      <span className="flex flex-1 items-center gap-1 text-sm text-gray-600">
        <Icons.Info className="size-4 shrink-0" aria-hidden="true" />
        {label}
      </span>
    </Tooltip>
  );
};

const getVisibleOriginals = (row: DisplayNonModeledRow) => {
  const enrollmentDiffers =
    row.originalEnrollmentValue != null &&
    Number.isFinite(row.originalEnrollmentValue) &&
    row.enrollmentValue !== row.originalEnrollmentValue;
  const rateDiffers =
    row.originalTuitionDiscountRateValue != null &&
    Number.isFinite(row.originalTuitionDiscountRateValue) &&
    row.tuitionDiscountRateValue !== row.originalTuitionDiscountRateValue;

  if (!enrollmentDiffers && !rateDiffers) {
    return {
      originalEnrollment: undefined,
      originalTuitionDiscountRate: undefined,
    };
  }

  return {
    originalEnrollment: row.originalEnrollmentValue,
    originalTuitionDiscountRate: row.originalTuitionDiscountRateValue,
  };
};

const renderRepackageCell = (
  row: DisplayNonModeledRow,
  onToggle: GetNonModeledTableSettingsParams['onRepackageToggle'],
  onUndo: GetNonModeledTableSettingsParams['onRowUndo'],
  readOnly?: boolean
) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-3">
      <Checkbox
        labelFor={`repackage-${row.key}`}
        label={`Repackage with new regimes for ${row.population}`}
        labelTextClass="sr-only"
        checked={row.repackageWithNewRegimes}
        onChange={() => onToggle(row, !row.repackageWithNewRegimes)}
        disabled={readOnly || !row.resultKey}
        inputCustomClass="size-4"
      />
      {renderUsingPreviousAwards(row)}
      {row.isDirty && (
        <IconButton
          variant="reserve-text"
          size="xs"
          icon={<Icons.Undo />}
          aria-label={`Undo changes for ${row.population}`}
          onClick={() => onUndo(row)}
          className="ml-auto shrink-0"
        />
      )}
    </div>
  </div>
);

export const getNonModeledTableSettings = ({
  onEnrollmentChange,
  onDiscountRateChange,
  onRepackageToggle,
  onRepackageToggleAll,
  onRowUndo,
  allRepackaged,
  someRepackaged,
  readOnly,
}: GetNonModeledTableSettingsParams): ColumnDef<DisplayNonModeledRow>[] => [
  {
    id: 'population',
    header: () => <span>Populations</span>,
    cell: ({ row }) => (
      <span className="text-base font-semibold text-gray-700 leading-6">
        {row.original.population}
      </span>
    ),
    headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
    className: 'w-[20%] px-2.5 py-2',
    enableSorting: false,
  },
  {
    id: 'enrollment',
    header: () => <span>Est. Enrollment</span>,
    cell: ({ row }) => {
      const { originalEnrollment } = getVisibleOriginals(row.original);

      return (
        <EditableEnrollmentCell
          value={row.original.enrollmentValue}
          onChange={(value) => onEnrollmentChange(row.original, value)}
          isDirty={
            row.original.enrollmentValue !==
            row.original.baselineEnrollmentValue
          }
          disabled={readOnly || row.original.repackageWithNewRegimes}
          originalValue={originalEnrollment}
        />
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[14%] px-2.5 py-2',
    enableSorting: false,
  },
  {
    id: 'netTuitionRevenue',
    header: () => <span>Est. NTR</span>,
    cell: ({ row }) => renderValueCell(row.original, 'netTuitionRevenue'),
    headerClassName: commonHeaderStyles,
    className: 'w-[13%] px-2.5 py-2',
    enableSorting: false,
  },
  {
    id: 'institutionalAid',
    header: () => <span>Est. Inst. Aid</span>,
    cell: ({ row }) => renderValueCell(row.original, 'institutionalAid'),
    headerClassName: commonHeaderStyles,
    className: 'w-[13%] px-2.5 py-2',
    enableSorting: false,
  },
  {
    id: 'tuitionDiscountRate',
    header: () => <span>Est. Tuition Discount</span>,
    cell: ({ row }) => {
      const { originalTuitionDiscountRate } = getVisibleOriginals(row.original);

      return (
        <EditableDiscountRateCell
          value={row.original.tuitionDiscountRateValue}
          onChange={(value) => onDiscountRateChange(row.original, value)}
          isDirty={
            row.original.tuitionDiscountRateValue !==
            row.original.baselineTuitionDiscountRateValue
          }
          disabled={readOnly || row.original.repackageWithNewRegimes}
          originalValue={originalTuitionDiscountRate}
        />
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[16%] px-2.5 py-2',
    enableSorting: false,
  },
  {
    id: 'repackage',
    header: () => (
      <Checkbox
        labelFor="repackage-select-all"
        label="Repackage with new regimes"
        checked={allRepackaged}
        indeterminate={someRepackaged && !allRepackaged}
        onChange={() => onRepackageToggleAll(!allRepackaged)}
        disabled={readOnly}
        inputCustomClass="size-4"
        labelTextClass="!text-xs font-semibold text-navy-600 tracking-[1.2px]"
      />
    ),
    cell: ({ row }) =>
      renderRepackageCell(row.original, onRepackageToggle, onRowUndo, readOnly),
    headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
    className: 'w-[24%] px-2.5 py-2',
    enableSorting: false,
  },
];
